import type { ReactNode } from "react";

import { PROVENANCE_WARNING_SENTENCE, needsProvenanceWarning, type RecordFieldProvenance } from "@/components/record/fields";
import { cx } from "@/lib/cx";

export interface FieldRowProps {
  label: string;
  /** The resting value content, or an actively-editing field — whichever
   * `RecordPage` (the caller) already resolved. `FieldRow` itself never
   * decides what renders here; it only lays it out. */
  children: ReactNode;
  provenance?: RecordFieldProvenance | undefined;
  /** Overrides the default `PROVENANCE_WARNING_SENTENCE` — see
   * `RecordField.warningSentence`'s own doc comment for why. */
  warningSentence?: string | undefined;
  /** Present only when this field is editable and the account is not
   * Read-only — activates the field the same way clicking `InlineEditCell`
   * itself does, from anywhere in the row (VC-2.5-07: "the whole row is
   * the edit tap target"), including the label, which sits outside
   * `InlineEditCell`'s own click handling. */
  onActivate?: (() => void) | undefined;
  /**
   * "card" (Owner-requested, 2026-08-27): the "More details" section's own
   * presentation, label stacked above value inside a bordered tile rather
   * than the label/value side-by-side row `RecordPage.tsx`'s always-visible
   * primary fields still use — a `RecordPage`-level grid arranges these
   * tiles, this component only knows how to lay out its own single field
   * once told which shape to take. Defaults to the original row shape so
   * every other caller (there is only one other today) is unaffected.
   */
  layout?: "row" | "card";
}

/**
 * `DESIGN.md`'s `field-row` component (Story 2.5): 44px minimum, label in a
 * 180px left column on desktop, label above the value on phone, an empty
 * optional field's label rendered with an em dash rather than hidden
 * (VC-2.5-09, the em dash itself comes from `DataTable.tsx`'s shared
 * `renderCell`, not re-derived here), and AD-10's provenance marker
 * (VC-2.5-17/18) — the dotted warning underline plus the exact sentence
 * when the field's provenance is an unconfirmed legal default, nothing at
 * all otherwise.
 *
 * `data-inline-edit-boundary` (paired with the same attribute value
 * `InlineEditCell`'s `<td>` wrapper effectively provides in a table):
 * `InlineEditCell`'s own `Escape`/`Tab` handling restores focus to
 * `.closest("td, [data-inline-edit-boundary]")` after reverting to resting
 * state — widened, not duplicated, so this row is a legitimate focus
 * target for that same mechanism outside a `<table>`.
 */
export function FieldRow({ label, children, provenance, warningSentence, onActivate, layout = "row" }: FieldRowProps) {
  const warn = needsProvenanceWarning(provenance);

  if (layout === "card") {
    return (
      <div
        data-inline-edit-boundary
        tabIndex={-1}
        onClick={onActivate}
        className={cx(
          "flex min-h-tap-target-min flex-col gap-1 rounded-lg bg-surface p-4 shadow-card",
          onActivate ? "cursor-pointer" : undefined,
        )}
      >
        {/* Owner-caught, 2026-08-28: this card's own label is its title —
         * `text-ink-secondary`/`font-medium` (the row layout's own muted
         * treatment, still correct there since the row's fixed-width left
         * column reads as a caption beside its value) read as too faint
         * once promoted to a card heading sitting alone above its value.
         * Darkened to `text-ink` and bolded to `font-semibold`, scoped to
         * the card layout only. */}
        <span className="text-label font-semibold text-ink">{label}</span>
        <span
          className={cx(
            "text-body text-ink",
            warn ? "underline decoration-warning decoration-dotted decoration-1 underline-offset-[3px]" : undefined,
          )}
        >
          {children}
        </span>
        {warn ? <p className="mt-1 text-meta text-warning">{warningSentence ?? PROVENANCE_WARNING_SENTENCE}</p> : null}
      </div>
    );
  }

  return (
    <div
      data-inline-edit-boundary
      tabIndex={-1}
      onClick={onActivate}
      className={cx(
        "flex min-h-tap-target-min flex-col justify-center gap-1 py-2 md:flex-row md:items-center md:gap-4",
        onActivate ? "cursor-pointer" : undefined,
      )}
    >
      <span className="w-field-label-col shrink-0 text-label font-medium text-ink-secondary">{label}</span>
      <div className="min-w-0 flex-1">
        <span
          className={cx(
            "text-body text-ink",
            warn ? "underline decoration-warning decoration-dotted decoration-1 underline-offset-[3px]" : undefined,
          )}
        >
          {children}
        </span>
        {warn ? <p className="mt-1 text-meta text-warning">{warningSentence ?? PROVENANCE_WARNING_SENTENCE}</p> : null}
      </div>
    </div>
  );
}
